import { toast } from 'sonner';
import { addProduct, gradeItem, rejectItem } from 'services/api/product';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { useProductStore } from '../store/productStore';
import { useReceiptStore } from '../store/receiptStore';
import { GradeData, ProductLine, SubmitApiResult } from 'types';
import { SCREEN_TYPES } from 'utils/constants';

const onGradeComplete = (updatedProduct?: ProductLine): void => {
  const { setCurrentProduct } = useProductStore.getState();
  const { receiptData, setReceiptData } = useReceiptStore.getState();
  const { setCurrentScreen } = useAppStore.getState();
  if (updatedProduct) {
    setCurrentProduct(updatedProduct);
    // Update receiptData with the graded line
    if (receiptData) {
      const updatedLines =
        receiptData.lines?.map((line) =>
          line?.receipt_line === updatedProduct.receipt_line
            ? { ...line, ...updatedProduct, receipt_ind: 'y' }
            : line
        ) || [];
      setReceiptData({ ...receiptData, lines: updatedLines });
    }
  }
  setCurrentScreen(SCREEN_TYPES.DISPOSITION);
};

export const submitCurrentGradeItem = async (): Promise<SubmitApiResult> => {
  const { user, stationId } = useAuthStore.getState();
  const {
    currentProduct: productLine,
    isProductNew,
    productCondition,
    setCurrentProduct,
  } = useProductStore.getState();
  const { receiptData, setReceiptData } = useReceiptStore.getState();

  try {
    const gradeData: GradeData = {
      user_id: user.id,
      station_id: stationId,
      transaction_id: productLine.transaction_id,
      receipt_line: productLine.receipt_line,
      is_new: isProductNew,
      product_condition: productCondition,
    };

    const result =
      productLine.expected_ind === 'y' ? await gradeItem(gradeData) : await addProduct(gradeData);

    if (result?.responseState === 'confirmed') {
      toast.success('Item graded successfully');
      // Update product with disposition_stage from response
      const updatedProduct = {
        ...productLine,
        ...(result.receipt_line || {}),
        disposition_stage: result.receipt_line?.disposition_stage,
      } as ProductLine;
      onGradeComplete(updatedProduct);
    } else {
      toast.error(result?.responseMsg || 'Failed to grade item');
      return { success: false };
    }

    const updatedProduct = {
      ...productLine,
      ...(result.receipt_line || {}),
      disposition_stage: result.receipt_line?.disposition_stage,
    } as ProductLine;

    setCurrentProduct(updatedProduct);

    if (receiptData) {
      const updatedLines =
        receiptData.lines?.map((line) =>
          line?.receipt_line === updatedProduct.receipt_line
            ? { ...line, ...updatedProduct, receipt_ind: 'y' }
            : line
        ) || [];
      setReceiptData({ ...receiptData, lines: updatedLines });
    }

    return { success: true };
  } catch {
    toast.error('Network error. Please try again.');
    return { success: false };
  }
};

export const rejectCurrentGradeItem = async (): Promise<SubmitApiResult> => {
  const { user, stationId } = useAuthStore.getState();
  const { currentProduct: productLine } = useProductStore.getState();
  try {
    const rejectData = {
      transaction_id: productLine?.transaction_id,
      product_id: productLine?.product_id,
      reject_note: `Item rejected - ${productLine?.product_id || 'N/A'}`,
      user_id: user.id,
      station_id: stationId,
    };
    const result = await rejectItem(rejectData);
    if (result?.responseState === 'confirmed') {
      toast.success('Item rejected successfully');
      const updatedProduct = {
        ...productLine,
        disposition_stage: 'pr',
        receipt_ind: 'y',
      } as ProductLine;
      onGradeComplete(updatedProduct);
      return { success: true };
    } else {
      toast.error(result?.responseMsg || 'Failed to reject item');
      return { success: false };
    }
  } catch {
    toast.error('Network error. Please try again.');
    return { success: false };
  }
};
